import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { browserDB } from '@/services/browser-storage/indexedDB';

// Define simplified types for CSR
interface UserStats {
  totalApps: number;
  publicApps: number;
  totalStars: number;
  totalViews: number;
}

interface UserActivity {
  id: string;
  type: 'app_created' | 'app_updated' | 'app_starred';
  appId: string;
  appTitle: string;
  timestamp: Date;
}

export function useUserStats() {
  const { isAuthenticated, user } = useAuth();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    try {
      // For CSR architecture, calculate stats from browser storage
      const projects = await browserDB.getUserProjects(user.id);

      // Simplified stats calculation (no public/private distinction in CSR yet)
      const stats: UserStats = {
        totalApps: projects.length,
        publicApps: 0,
        totalStars: 0,
        totalViews: 0
      };

      setStats(stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
}

export function useUserActivity() {
  const { isAuthenticated, user } = useAuth();
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActivity = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    try {
      // For CSR architecture, generate activity from browser storage
      const projects = await browserDB.getUserProjects(user.id);

      // Create simplified activity entries
      const activities: UserActivity[] = projects.map(project => ({
        id: `activity_${project.id}`,
        type: 'app_created' as const,
        appId: String(project.id ?? project.agentId),
        appTitle: project.title,
        timestamp: project.createdAt
      }));

      // Sort by timestamp (most recent first)
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      setActivities(activities.slice(0, 10)); // Limit to 10 most recent
    } catch (err) {
      console.error('Error fetching activity:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch activity');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  return { activities, loading, error, refetch: fetchActivity };
}